import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCustomPageTitle } from '../../hooks/usePageTitle';
import { DetailedEstimate } from '../../components/estimation/detailed/DetailedEstimate';
import { QuickEstimate } from '../../components/estimation/quick/QuickEstimate';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import ErrorMessage from '../../components/ui/ErrorMessage';
import { useAuth } from '../../contexts/AuthContext';
import { getEstimate } from '../../services/estimateService';

const EditEstimate = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  useCustomPageTitle('Modification d\'estimation');
  const [estimate, setEstimate] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      // 1) Try localStorage first (fast path)
      const savedEstimate = localStorage.getItem('editEstimate');
      if (savedEstimate) {
        try {
          const parsedEstimate = JSON.parse(savedEstimate);
          setEstimate(parsedEstimate);
          setLoading(false);
          return;
        } catch {
          // localStorage corrupt — fall through to Firebase
        }
      }

      // 2) Fallback: load from Firebase
      if (!id || !user) {
        setError('Estimation non trouvée');
        setLoading(false);
        return;
      }

      try {
        const firebaseEstimate = await getEstimate(user.uid, id);
        if (firebaseEstimate) {
          setEstimate({ ...firebaseEstimate, _editMode: true, _estimateId: id });
        } else {
          navigate('/dashboard', { replace: true, state: { error: 'Estimation non trouvée' } });
        }
      } catch (err) {
        setError('Erreur lors du chargement de l\'estimation');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id, user]);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;
  if (!estimate) return null;

  // Pour les estimations détaillées
  if (estimate.type === 'detailed') {
    const { _editMode, _estimateId, ...estimateData } = estimate;
    return (
      <DetailedEstimate
        initialData={estimateData}
        isEditMode={_editMode === true}
        estimateId={_estimateId}
      />
    );
  }

  // Pour les estimations rapides
  return <QuickEstimate initialData={estimate} />;
};

export default EditEstimate;